import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { IconArrowForwardUp, IconChartBar, IconDisc, IconDownload, IconEye, IconEyeOff, IconFileMusic, IconLink, IconMicrophone, IconMicrophone2, IconMovie, IconMusic, IconPencil, IconRepeat, IconStar, IconTrash, IconWand, } from "@tabler/icons-react";
/** Резервные наборы действий по типу строки — зеркало бэкового allowed_actions
 *  (см. domain/constants/audio_actions.py). Используется, только если API не
 *  прислал `available_actions` (старый бэк / лёгкие источники плеера). */
const NORMAL_ACTIONS = [
    "download_mp3",
    "download_wav",
    "rename",
    "favorite",
    "chart_toggle",
    "share",
    "make_instrumental",
    "extend",
    "video_cover",
    "karaoke",
    "repeat",
    "hide",
    "delete",
];
const VOCAL_ACTIONS = [
    "download_mp3",
    "download_wav",
    "rename",
    "favorite",
    "chart_toggle",
    "share",
    "extend",
    "video_cover",
    "karaoke",
    "hide",
    "delete",
];
const INSTRUMENTAL_ACTIONS = [
    "download_mp3",
    "download_wav",
    "rename",
    "favorite",
    "chart_toggle",
    "share",
    "extend",
    "video_cover",
    "hide",
    "delete",
];
/** Набор действий по умолчанию для типа аудио (фолбэк без `available_actions`). */
export const defaultActionsForType = (type) => {
    if (type === "instrumental")
        return INSTRUMENTAL_ACTIONS;
    if (type === "vocal")
        return VOCAL_ACTIONS;
    return NORMAL_ACTIONS;
};
export const kindFromAudioType = (type) => type === "instrumental" ? "minus" : type === "vocal" ? "vocal" : "song";
/**
 * Собрать упорядоченный список действий трека. Порядок фиксирован (единый во
 * всех местах): скачать · WAV · переименовать · избранное · чарт · ссылки ·
 * минус · ИИ-голос · релиз · продлить · видеообложка · караоке · повторить ·
 * скрыть · удалить.
 */
export function buildTrackActions(ctx) {
    const ru = ctx.language === "ru";
    const has = (a) => ctx.actions.includes(a);
    const items = [];
    // Скачать mp3.
    if (has("download_mp3") && ctx.onDownload) {
        items.push({
            key: "download_mp3",
            icon: _jsx(IconDownload, { size: 16 }),
            label: ctx.kind === "minus"
                ? ru
                    ? "Скачать минус"
                    : "Download instrumental"
                : ctx.kind === "vocal"
                    ? ru
                        ? "Скачать вокал"
                        : "Download vocal"
                    : ru
                        ? "Скачать песню"
                        : "Download song",
            onClick: ctx.onDownload,
        });
    }
    // WAV (бесплатно).
    if (has("download_wav") && ctx.onWav) {
        items.push({
            key: "download_wav",
            icon: _jsx(IconFileMusic, { size: 16 }),
            label: ctx.wavBusy
                ? ru
                    ? "Готовим WAV…"
                    : "Preparing WAV…"
                : ctx.wavReady
                    ? ru
                        ? "Скачать WAV"
                        : "Download WAV"
                    : ru
                        ? "Получить WAV"
                        : "Get WAV",
            onClick: ctx.onWav,
            disabled: ctx.wavDisabled,
        });
    }
    if (has("rename") && ctx.onRename) {
        items.push({
            key: "rename",
            icon: _jsx(IconPencil, { size: 16 }),
            label: ru ? "Переименовать" : "Rename",
            onClick: ctx.onRename,
        });
    }
    if (has("favorite") && ctx.onFavorite) {
        items.push({
            key: "favorite",
            icon: (_jsx(IconStar, { size: 16, fill: ctx.favorited ? "currentColor" : "none" })),
            label: ctx.favorited
                ? ru
                    ? "Убрать из избранного"
                    : "Remove from favorites"
                : ru
                    ? "В избранное"
                    : "Add to favorites",
            onClick: ctx.onFavorite,
        });
    }
    // Чарт — в меню только там, где нет inline-тумблера (мобилка списка / плеер).
    if (has("chart_toggle") && ctx.onChart && ctx.showChartInMenu) {
        items.push({
            key: "chart_toggle",
            icon: _jsx(IconChartBar, { size: 16 }),
            label: ctx.isShowInCharts
                ? ru
                    ? "Убрать из чарта"
                    : "Remove from chart"
                : ru
                    ? "Добавить в чарт"
                    : "Add to chart",
            onClick: ctx.onChart,
        });
    }
    if (has("share") && ctx.onShare) {
        items.push({
            key: "share",
            icon: _jsx(IconLink, { size: 16 }),
            label: ru ? "Ссылки на подарок" : "Gift links",
            onClick: ctx.onShare,
        });
    }
    // Создать/Показать/Скачать минус (только у песни).
    if (has("make_instrumental") && ctx.onMakeInstrumental) {
        const ready = !!ctx.instrReady;
        const busy = !!ctx.instrBusy;
        const readyLabel = ctx.instrReadyMode === "download"
            ? ru
                ? "Скачать минус"
                : "Download instrumental"
            : ru
                ? "Показать минус"
                : "Show instrumental";
        items.push({
            key: "make_instrumental",
            icon: ready ? _jsx(IconMusic, { size: 16 }) : _jsx(IconWand, { size: 16 }),
            label: busy
                ? ru
                    ? "Готовим минус…"
                    : "Preparing…"
                : ready
                    ? readyLabel
                    : ru
                        ? "Создать минус"
                        : "Create instrumental",
            onClick: ctx.onMakeInstrumental,
            disabled: busy,
            accent: !ready && !busy,
        });
    }
    // Создать ИИ-голос (персону) из готового трека — не из available_actions
    // (отдельная V-фича поверх R-модели), доступно только у песни с готовым аудио.
    if (ctx.canVoicePersona && ctx.onVoicePersona) {
        items.push({
            key: "voice_persona",
            icon: _jsx(IconMicrophone, { size: 16 }),
            label: ru ? "Создать ИИ-голос из трека" : "Create AI voice from track",
            onClick: ctx.onVoicePersona,
        });
    }
    // Релиз трека на площадках — тоже не из available_actions (отдельная фича
    // поверх R-модели): доступен владельцу готовой песни, минус/вокал не релизим.
    if (ctx.canRelease && ctx.onRelease) {
        items.push({
            key: "release",
            icon: _jsx(IconDisc, { size: 16 }),
            label: ru ? "Релиз трека" : "Release track",
            onClick: ctx.onRelease,
            highlighted: ctx.highlightRelease,
        });
    }
    if (has("extend") && ctx.onExtend) {
        items.push({
            key: "extend",
            icon: _jsx(IconArrowForwardUp, { size: 16 }),
            label: ru ? "Продлить трек" : "Extend",
            onClick: ctx.onExtend,
        });
    }
    if (has("video_cover") && ctx.onVideoCover) {
        items.push({
            key: "video_cover",
            icon: _jsx(IconMovie, { size: 16 }),
            label: ru ? "Видеообложка" : "Video cover",
            onClick: ctx.onVideoCover,
        });
    }
    if (has("karaoke") && ctx.onKaraoke) {
        items.push({
            key: "karaoke",
            icon: _jsx(IconMicrophone2, { size: 16 }),
            label: ru ? "Караоке-клип" : "Karaoke",
            onClick: ctx.onKaraoke,
        });
    }
    if (has("repeat") && ctx.onRepeat) {
        items.push({
            key: "repeat",
            icon: _jsx(IconRepeat, { size: 16 }),
            label: ctx.repeatBusy
                ? ru
                    ? "Загружаем…"
                    : "Loading…"
                : ru
                    ? "Повторить генерацию"
                    : "Repeat generation",
            onClick: ctx.onRepeat,
            disabled: ctx.repeatBusy,
        });
    }
    if (has("hide") && ctx.onHide) {
        items.push({
            key: "hide",
            icon: ctx.isShow ? _jsx(IconEyeOff, { size: 16 }) : _jsx(IconEye, { size: 16 }),
            label: ctx.isShow ? (ru ? "Скрыть" : "Hide") : ru ? "Показать" : "Unhide",
            onClick: ctx.onHide,
        });
    }
    if (has("delete") && ctx.onDelete) {
        items.push({
            key: "delete",
            icon: _jsx(IconTrash, { size: 16 }),
            label: ru ? "Удалить" : "Delete",
            onClick: ctx.onDelete,
            danger: true,
        });
    }
    return items;
}
